import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/remix/$id/plan')({
  component: PlanBreakdownPage,
})

type DecisionType = 'matched' | 'substituted' | 'missing_required' | 'missing_optional'

type SlotDecision = {
  id: string
  slotId: string
  decision: DecisionType
  kitItemId: string | null
  adaptedFinish: string | null
  adaptedCoverage: string | null
  adaptedIntensity: string | null
  explanation: string
  createdAt: string
}

type OwnedAlternative = {
  id: string
  category: string
  colorFamily: string | null
  finish: string | null
  matchType: 'exact_color' | 'related_color' | 'category_only'
  finishCompatible: boolean
}

type MissingProduct = {
  slotId: string
  slotLabel: string
  category: string
  shade: string
  explanation: string
  ownedAlternatives: OwnedAlternative[]
}

type SlotInfo = {
  id: string
  zone: string
  label: string
  shade: string
  productName?: string | null
}

type PlanData = {
  id: string
  goals: string[]
  status: string
  slotDecisions?: SlotDecision[]
  lookSlots?: SlotInfo[]
}

type TabType = 'owned' | 'substituted' | 'missing'

const TAB_LABELS: Record<TabType, string> = {
  owned: 'Owned',
  substituted: 'Substituted',
  missing: 'Missing',
}

const DECISION_BADGES: Record<DecisionType, { label: string; className: string }> = {
  matched: { label: 'Matched', className: 'bg-success/10 text-success' },
  substituted: { label: 'Substituted', className: 'bg-warning/10 text-warning' },
  missing_required: { label: 'Required', className: 'bg-danger/10 text-danger' },
  missing_optional: { label: 'Optional', className: 'bg-muted text-muted-foreground' },
}

const MATCH_TYPE_LABELS: Record<string, string> = {
  exact_color: 'Exact color match',
  related_color: 'Similar color',
  category_only: 'Same category',
}

function PlanBreakdownPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [creatingTryon, setCreatingTryon] = React.useState(false)
  const [tryonError, setTryonError] = React.useState<string | null>(null)
  const [plan, setPlan] = React.useState<PlanData | null>(null)
  const [missingProducts, setMissingProducts] = React.useState<MissingProduct[]>([])
  const [activeTab, setActiveTab] = React.useState<TabType>('owned')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const fetchData = React.useCallback(async () => {
    try {
      const sessionRes = await fetch('/api/auth/session-state')
      const session = sessionRes.ok
        ? ((await sessionRes.json()) as { authenticated?: boolean })
        : { authenticated: false }
      if (session.authenticated !== true) {
        setError('Sign in to open a remix plan.')
        return
      }

      const [planRes, missingRes] = await Promise.all([
        fetch(`/api/v1/remix-plans/${id}`),
        fetch(`/api/v1/remix-plans/${id}/missing-products`),
      ])

      if (!planRes.ok) {
        throw new Error('Failed to fetch plan')
      }
      const planData = await planRes.json()
      setPlan(planData)

      if (missingRes.ok) {
        const missingData = await missingRes.json()
        setMissingProducts(missingData ?? [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plan')
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    fetchData()
  }, [fetchData])

  const decisions = plan?.slotDecisions ?? []

  const ownedDecisions = decisions.filter((d) => d.decision === 'matched')
  const substitutedDecisions = decisions.filter((d) => d.decision === 'substituted')
  const missingDecisions = decisions.filter(
    (d) => d.decision === 'missing_required' || d.decision === 'missing_optional',
  )

  const tabs: TabType[] = ['owned', 'substituted', 'missing']
  const tabCounts: Record<TabType, number> = {
    owned: ownedDecisions.length,
    substituted: substitutedDecisions.length,
    missing: missingDecisions.length,
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted">Loading plan breakdown...</p>
      </div>
    )
  }

  if (error || !plan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-danger">{error ?? 'Plan not found'}</p>
        <a href="/looks" className="text-primary hover:underline">
          Back to looks
        </a>
      </div>
    )
  }

  if (plan.status !== 'adapted') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-muted">This plan hasn't been adapted yet.</p>
        <a href={`/remix/${id}/goals`} className="text-primary hover:underline">
          Set goals first
        </a>
      </div>
    )
  }

  return (
    <div className="ootopia-pwa-page ootopia-pwa-stack">
      <div>
        <p className="ootopia-section-kicker">Your adapted recipe</p>
        <h1 className="mt-2 text-4xl font-black tracking-[-0.06em] ootopia-editorial-heading">
          A look built around your kit
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Review how each slot in the recipe maps to your kit
        </p>
      </div>

      {/* Summary bar */}
      <div className="ootopia-pwa-surface ootopia-pwa-summary-grid p-3">
        <SummaryBadge label="Owned" count={ownedDecisions.length} variant="success" />
        <SummaryBadge label="Substituted" count={substitutedDecisions.length} variant="warning" />
        <SummaryBadge label="Missing" count={missingDecisions.length} variant="danger" />
      </div>

      {/* Tab bar */}
      <div className="ootopia-pwa-tabs" role="tablist" aria-label="Recipe matches">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 rounded-xl py-2 text-sm font-medium transition-all ootopia-tap-target ${
              activeTab === tab
                ? 'ootopia-gradient-accent'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {TAB_LABELS[tab]}
            {tabCounts[tab] > 0 && <span className="ml-1 text-xs">({tabCounts[tab]})</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="space-y-3">
        {activeTab === 'owned' && (
          <DecisionList decisions={ownedDecisions} emptyMessage="No owned products matched" />
        )}
        {activeTab === 'substituted' && (
          <DecisionList decisions={substitutedDecisions} emptyMessage="No substitutions needed" />
        )}
        {activeTab === 'missing' && (
          <MissingList decisions={missingDecisions} missingProducts={missingProducts} />
        )}
      </div>

      {/* Action bar */}
      <div className="ootopia-pwa-sticky-action">
        {tryonError && <p className="text-sm text-danger text-center">{tryonError}</p>}
        <button
          type="button"
          onClick={async () => {
            setCreatingTryon(true)
            setTryonError(null)
            try {
              const res = await fetch(`/api/v1/remix-plans/${id}/tryons`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
              })
              if (!res.ok) {
                const data = await res.json()
                throw new Error(data.error ?? 'Failed to create try-on')
              }
              const data = await res.json()
              navigate({ to: '/tryon/$id', params: { id: data.id } })
            } catch (err) {
              setTryonError(err instanceof Error ? err.message : 'Failed to create try-on')
            } finally {
              setCreatingTryon(false)
            }
          }}
          disabled={creatingTryon}
          className="w-full rounded-xl bg-primary py-3 font-medium text-primary-foreground disabled:opacity-50"
        >
          {creatingTryon ? 'Creating...' : 'Create Try-On'}
        </button>
        <a
          href={`/remix/${id}/goals`}
          className="text-center text-sm text-muted hover:text-primary"
        >
          Edit goals
        </a>
      </div>
    </div>
  )
}

function SummaryBadge({
  label,
  count,
  variant,
}: {
  label: string
  count: number
  variant: 'success' | 'warning' | 'danger'
}) {
  const variantClasses = {
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    danger: 'bg-danger/10 text-danger',
  }

  return (
    <div className={`flex-1 rounded-lg p-2 text-center ${variantClasses[variant]}`}>
      <p className="text-lg font-bold">{count}</p>
      <p className="text-xs">{label}</p>
    </div>
  )
}

function DecisionList({
  decisions,
  emptyMessage,
}: {
  decisions: SlotDecision[]
  emptyMessage: string
}) {
  if (decisions.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center">
        <p className="text-muted">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {decisions.map((d) => (
        <DecisionCard key={d.id} decision={d} />
      ))}
    </div>
  )
}

function DecisionCard({ decision }: { decision: SlotDecision }) {
  const badge = DECISION_BADGES[decision.decision]

  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{decision.slotId}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-sm text-muted mt-1">{decision.explanation}</p>
        </div>
      </div>
      {(decision.adaptedFinish || decision.adaptedCoverage || decision.adaptedIntensity) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {decision.adaptedFinish && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
              Finish: {decision.adaptedFinish}
            </span>
          )}
          {decision.adaptedCoverage && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
              Coverage: {decision.adaptedCoverage}
            </span>
          )}
          {decision.adaptedIntensity && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
              Intensity: {decision.adaptedIntensity}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function MissingList({
  decisions,
  missingProducts,
}: {
  decisions: SlotDecision[]
  missingProducts: MissingProduct[]
}) {
  if (decisions.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center">
        <p className="text-muted">All products accounted for</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {decisions.map((d) => {
        const product = missingProducts.find((p) => p.slotId === d.slotId)
        return <MissingCard key={d.id} decision={d} missingProduct={product} />
      })}
    </div>
  )
}

function MissingCard({
  decision,
  missingProduct,
}: {
  decision: SlotDecision
  missingProduct?: MissingProduct
}) {
  const badge = DECISION_BADGES[decision.decision]
  const alternatives = missingProduct?.ownedAlternatives ?? []

  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{decision.slotId}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-sm text-muted mt-1">{decision.explanation}</p>
          {missingProduct && (
            <p className="text-sm mt-1">
              Looking for: <span className="font-medium">{missingProduct.shade}</span>
            </p>
          )}
        </div>
      </div>

      {/* Owned alternatives (Duplicate Guard) */}
      {alternatives.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-2">You might already own:</h3>
          <div className="space-y-2">
            {alternatives.map((alt) => (
              <div key={alt.id} className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium capitalize">{alt.category}</p>
                  {alt.colorFamily && (
                    <p className="text-xs text-muted capitalize">{alt.colorFamily}</p>
                  )}
                  <p className="text-xs text-primary mt-0.5">
                    {MATCH_TYPE_LABELS[alt.matchType]}
                    {alt.finishCompatible && ' • Finish compatible'}
                  </p>
                </div>
                <span className="ootopia-pwa-status ootopia-pwa-status--quiet">
                  Suggested substitute
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {alternatives.length === 0 && missingProduct && (
        <div className="mt-4 rounded-lg bg-muted p-3">
          <p className="text-sm text-muted">No similar products in your kit.</p>
          <p className="text-xs text-muted mt-1">Add a product in My Kit to complete this step.</p>
        </div>
      )}
    </div>
  )
}
